import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  createAttempt,
  getActiveCourseQuestions,
  getCourses,
  getEnrollment,
  getUserAttempts,
} from "../lib/db";
import { pickRandom } from "../lib/grading";
import { accessExpiry, isAccessExpired } from "../lib/access";
import type { Attempt, Course, Enrollment } from "../lib/types";
import "./Tests.css";

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function Tests() {
  const { user, email, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [allCourses, enr, userAttempts] = await Promise.all([
          getCourses(),
          email ? getEnrollment(email) : Promise.resolve(null),
          user ? getUserAttempts(user.uid) : Promise.resolve([]),
        ]);
        if (!alive) return;
        setCourses(allCourses);
        setEnrollment(enr);
        setAttempts(userAttempts);
      } catch (err) {
        console.error(err);
        if (alive) setError("No se pudieron cargar los cursos.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, email]);

  const visibleCourses = useMemo(
    () =>
      courses.filter(
        (c) => c.active && (isAdmin || !!enrollment?.courses?.[c.id]),
      ),
    [courses, enrollment, isAdmin],
  );

  // Expired (non-admin) courses are shown but locked.
  const expiredFor = (course: Course) =>
    !isAdmin && isAccessExpired(enrollment, course);
  const expiryDateFor = (course: Course) =>
    accessExpiry(enrollment?.enrolledAt?.[course.id], course.accessDurationDays);

  const attemptsByCourse = useMemo(() => {
    const map: Record<string, Attempt[]> = {};
    for (const a of attempts) (map[a.courseId] ??= []).push(a);
    return map;
  }, [attempts]);

  async function startAttempt(course: Course) {
    if (!user || !email) return;
    if (expiredFor(course)) {
      setError("Tu acceso a este curso ha caducado.");
      return;
    }
    setStarting(course.id);
    setError(null);
    try {
      const questions = await getActiveCourseQuestions(course.id);
      if (questions.length === 0) {
        setError("Este curso todavía no tiene preguntas.");
        setStarting(null);
        return;
      }
      const count = course.questionsPerAttempt || 15;
      const selected = pickRandom(questions, Math.min(count, questions.length));
      const attemptId = await createAttempt({
        userId: user.uid,
        userEmail: email,
        course,
        questionIds: selected.map((q) => q.id),
      });
      navigate(`/tests/attempt/${attemptId}`);
    } catch (err) {
      console.error(err);
      setError("No se pudo iniciar el test. Inténtalo de nuevo.");
      setStarting(null);
    }
  }

  if (loading) {
    return (
      <div className="tests">
        <div className="spinner" style={{ margin: "80px auto" }} />
      </div>
    );
  }

  return (
    <div className="tests">
      {/* Hero */}
      <section className="hero card">
        <div className="hero__text">
          <h1 className="hero__title">¡Hola!</h1>
          <p>
            Esta web está creada para que puedas poner a prueba tus
            conocimientos de forma rápida, sencilla y divertida.
          </p>
          <p>
            Realiza tantos tests como quieras y avanza a tu ritmo.
            <br />
            ¿Listo para empezar?
          </p>
        </div>
        <img className="hero__img" src="/horse-books.png" alt="" />
      </section>

      {error && <p className="tests__error">{error}</p>}

      {/* Courses */}
      <h2 className="section-title">Tus cursos</h2>
      {visibleCourses.length === 0 ? (
        <p className="tests__empty">
          Aún no tienes cursos disponibles. Cuando compres un curso aparecerá
          aquí.
        </p>
      ) : (
        <div className="course-grid">
          {visibleCourses.map((course) => {
            const cAttempts = attemptsByCourse[course.id] ?? [];
            const last = cAttempts[0]; // attempts are sorted desc
            const hasHistory = !!last;
            const pct = last ? Math.round((last.score / last.total) * 100) : 0;
            const expired = expiredFor(course);
            const expiry = expiryDateFor(course);
            return (
              <div key={course.id} className="course card">
                <div className="course__head">
                  <h3 className="course__title">{course.title}</h3>
                  {expired ? (
                    <span className="badge badge--expired">Acceso caducado</span>
                  ) : (
                    hasHistory && (
                      <span className="badge badge--done">Completado</span>
                    )
                  )}
                </div>
                <p className="course__desc">{course.description}</p>

                {expired && (
                  <p className="course__expired">
                    Tu acceso caducó{expiry ? ` el ${formatDate(expiry)}` : ""}.
                    Renueva tu inscripción para seguir practicando.
                  </p>
                )}

                {hasHistory ? (
                  <>
                    <div className="course__result">
                      <div className="course__result-row">
                        <span>Último resultado</span>
                        <strong>{pct}%</strong>
                      </div>
                      <div className="progress">
                        <div
                          className="progress__fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <small className="course__date">
                        Último intento: {formatDate(last.createdAt)}
                        {!expired && expiry
                          ? ` · Acceso hasta ${formatDate(expiry)}`
                          : ""}
                      </small>
                    </div>
                    <div className="course__actions">
                      <button
                        className="btn"
                        onClick={() =>
                          navigate(`/tests/review/${last.id}`)
                        }
                      >
                        Ver resultados
                      </button>
                      <button
                        className="btn btn--muted"
                        disabled={starting === course.id || expired}
                        onClick={() => startAttempt(course)}
                      >
                        {starting === course.id ? "Cargando…" : "Nuevo test"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="course__actions course__actions--single">
                    <button
                      className="btn btn--block"
                      disabled={starting === course.id || expired}
                      onClick={() => startAttempt(course)}
                    >
                      {starting === course.id ? "Cargando…" : "Comenzar"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Progress history */}
      {attempts.length > 0 && (
        <>
          <h2 className="section-title">Tu progreso</h2>
          <div className="card progress-table">
            <table>
              <thead>
                <tr>
                  <th>Curso</th>
                  <th>Resultado</th>
                  <th>Fecha de realización</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/tests/review/${a.id}`)}
                  >
                    <td>{a.courseTitle}</td>
                    <td>
                      {a.score}/{a.total}
                    </td>
                    <td>{formatDate(a.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
